// useJobs(limit = 100, { refetchMs = 5000, enabled }) → UseQueryResult<Job[]>     key ["jobs", limit]
// The list, newest first, refreshed every 5 s ("updated 2 s ago · every 5 s"). Raising the
// limit ("Load 50 more") keeps the previous rows on screen (isPlaceholderData → .is-stale).
// useChildJobs(parent, limit?) → Job[]   jobs whose parent_job_id is the parent's id, from the same list. It polls only
// while children can still appear (a parent with auto_analyze_children, until 3 min after it ended) or one still runs.
import { keepPreviousData, useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo } from "react";
import { api } from "@/lib/api";
import { isTerminal } from "@/lib/eventStore";
import type { Job } from "@/types";
import { keys } from "./keys";
import { useClock } from "./useClock";

const JOBS_POLL_MS = 5000;
const CHILD_WINDOW_MS = 3 * 60_000;

export function useJobs(limit = 100, opts: { refetchMs?: number | false; enabled?: boolean } = {}) {
  return useQuery({
    queryKey: keys.jobs(limit),
    queryFn: async (): Promise<Job[]> => (await api.jobs(limit)).jobs ?? [],
    refetchInterval: opts.refetchMs ?? JOBS_POLL_MS,
    placeholderData: keepPreviousData,
    enabled: opts.enabled ?? true,
  });
}

const NONE: Job[] = [];

type Parent = Pick<Job, "id" | "auto_analyze_children" | "ended_at">;

const childrenOf = (parentId: string | undefined, list: readonly Job[] | undefined): Job[] =>
  parentId && list ? list.filter((j) => j.parent_job_id === parentId) : NONE;

export function useChildJobs(parent: Parent | undefined, limit = 100): Job[] {
  const qc = useQueryClient();
  const now = useClock(60_000);
  const ended = parent?.ended_at ? Date.parse(parent.ended_at) : NaN;
  const mayGrow = !!parent?.auto_analyze_children && (!Number.isFinite(ended) || now - ended < CHILD_WINDOW_MS);
  const running = childrenOf(parent?.id, qc.getQueryData<Job[]>(keys.jobs(limit))).some((j) => !isTerminal(j.state));
  const { data } = useJobs(limit, { enabled: !!parent, refetchMs: mayGrow || running ? JOBS_POLL_MS : false });
  return useMemo(() => childrenOf(parent?.id, data), [data, parent?.id]);
}
